import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Ticket, CreditCard, QrCode, CurrencyInr, IdentificationBadge, ArrowsClockwise, ChartLineUp } from "@phosphor-icons/react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const fmtINR = (n) => "₹" + ((+n || 0) >= 100000 ? `${(n/100000).toFixed(2)}L` : (+n || 0).toLocaleString("en-IN"));

const CARD_CAT_COLORS = ["#1E3A8A", "#3B82F6", "#22C55E", "#F59E0B", "#A855F7"];
const PASS_CAT_COLORS = ["#0EA5E9", "#22C55E", "#F97316", "#A855F7", "#EC4899", "#14B8A6", "#EAB308", "#6366F1", "#84CC16", "#06B6D4"];

export default function TicketsRevenue() {
  const [k, setK] = useState({});
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get("/dashboard/kpis").then(({ data }) => setK(data));
    api.get("/revenue/summary").then(({ data }) => setR(data));
  }, []);

  const cardRev = r?.smart_transit_card;
  const passRev = r?.smart_pass;
  const tx = r?.tickets;

  const hourly = Array.from({ length: 18 }, (_, i) => ({
    hour: `${(i + 5).toString().padStart(2, "0")}:00`,
    revenue: Math.round(Math.random() * 45000) + 8000,
  }));

  const streamMix = [
    { name: "Smart Pass", value: passRev?.total_revenue || 0, color: "#0EA5E9" },
    { name: "Transit Card (Issue)", value: cardRev?.total_issuance_revenue || 0, color: "#1E3A8A" },
    { name: "Transit Card (Top-up)", value: cardRev?.total_topup_revenue || 0, color: "#22C55E" },
    { name: "QR Tickets (today)", value: tx?.qr_tickets_today || 0, color: "#F97316" },
    { name: "Conductor Cash (today)", value: tx?.conductor_cash_today || 0, color: "#8B5CF6" },
  ].filter(s => s.value > 0);

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div>
        <div className="label-cap text-slate-500">Revenue Operations</div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Tickets & Revenue</h1>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total Revenue Today" value={r ? r.totals.today_str : "—"} color="#22C55E" icon={CurrencyInr}/>
        <KPI label="Smart Pass · Total" value={fmtINR(passRev?.total_revenue)} sub={`${passRev?.passes_issued || 0} passes issued`} color="#0EA5E9" icon={IdentificationBadge}/>
        <KPI label="Transit Card · Total" value={fmtINR((cardRev?.total_revenue) || 0)} sub={`${cardRev?.cards_issued || 0} cards issued`} color="#1E3A8A" icon={CreditCard}/>
        <KPI label="QR + Cash (today)" value={fmtINR(tx?.total_today)} sub="On-bus channels" color="#F97316" icon={QrCode}/>
      </div>

      {/* Stream breakdown: Pass and Card detail */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Smart Pass Revenue */}
        <div data-testid="rev-pass-card" className="ktcl-card p-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="label-cap text-slate-500">Smart Pass Revenue</div>
              <h3 className="font-display font-bold text-xl">Pass Issuance &amp; Renewals</h3>
            </div>
            <IdentificationBadge size={24} className="text-[#0EA5E9]" weight="duotone"/>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Stat label="Total" value={fmtINR(passRev?.total_revenue)} accent="#0EA5E9"/>
            <Stat label="Today" value={fmtINR(passRev?.today_revenue)} accent="#22C55E"/>
            <Stat label="From Renewals" value={fmtINR(passRev?.renewal_revenue)} accent="#A855F7"/>
          </div>
          <div className="text-[11px] text-slate-500 mb-2">Revenue by Pass Category</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={[...(passRev?.by_category || [])].sort((a,b)=>b.value-a.value)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false}/>
              <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94A3B8" tickFormatter={fmtINR}/>
              <YAxis type="category" dataKey="category" tick={{ fontSize: 10 }} width={130} stroke="#475569"/>
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
              <Bar dataKey="value" fill="#0EA5E9" radius={[0, 4, 4, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Smart Transit Card Revenue */}
        <div data-testid="rev-card-card" className="ktcl-card p-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="label-cap text-slate-500">Smart Transit Card Revenue</div>
              <h3 className="font-display font-bold text-xl">Issuance &amp; Top-ups</h3>
            </div>
            <CreditCard size={24} className="text-[#1E3A8A]" weight="duotone"/>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Stat label="Issuance Total" value={fmtINR(cardRev?.total_issuance_revenue)} accent="#1E3A8A"/>
            <Stat label="Top-up Total" value={fmtINR(cardRev?.total_topup_revenue)} accent="#22C55E"/>
            <Stat label="Today (combined)" value={fmtINR((cardRev?.issuance_today || 0) + (cardRev?.topup_today || 0))} accent="#F97316"/>
          </div>
          <div className="text-[11px] text-slate-500 mb-2">Revenue by Card Category</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={cardRev?.by_category || []} dataKey="value" nameKey="category" outerRadius={70} innerRadius={36} label={(e)=>`${e.category}`}>
                {(cardRev?.by_category || []).map((_, i) => <Cell key={i} fill={CARD_CAT_COLORS[i % CARD_CAT_COLORS.length]}/>)}
              </Pie>
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Channel mix + Hourly */}
      <div className="grid xl:grid-cols-2 gap-4">
        <div className="ktcl-card p-4">
          <div className="label-cap text-slate-500">Mix</div>
          <h3 className="font-display font-bold text-lg mb-3">Revenue stream split</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={[...streamMix].sort((a,b)=>b.value-a.value)} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false}/>
              <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94A3B8" tickFormatter={fmtINR}/>
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={170} stroke="#475569"/>
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {streamMix.map((c, i) => <Cell key={i} fill={c.color}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
            {streamMix.map(s => (
              <div key={s.name} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }}/>
                <span className="text-slate-600">{s.name}</span>
                <span className="font-semibold text-slate-800">{fmtINR(s.value)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="ktcl-card p-4">
          <div className="label-cap text-slate-500">Today</div>
          <h3 className="font-display font-bold text-lg mb-2">Hourly revenue (₹)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false}/>
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} stroke="#94A3B8"/>
              <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8"/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v) => fmtINR(v)}/>
              <Bar dataKey="revenue" fill="#1E3A8A" radius={[4, 4, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const KPI = ({ label, value, sub, color, icon: Icon }) => (
  <div className="ktcl-card ktcl-card-hover p-4">
    <div className="flex items-center justify-between">
      <div className="label-cap text-slate-500">{label}</div>
      <Icon size={18} weight="duotone" style={{ color }}/>
    </div>
    <div className="font-display text-2xl font-bold mt-2" style={{ color }}>{value}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">{label}</div>
    <div className="font-display font-bold text-lg tabular-nums" style={{ color: accent }}>{value}</div>
  </div>
);
